import React from "react";
import { Lock, Pause, Play, StopCircle, Unlock, Volume2, VolumeX } from "lucide-react";
import { RunMap } from "../../components/RunMap";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { activityTypeLabels, formatRunTime, gpsActivityTypes, intervalTypeNames } from "../../constants/running";

// Live running session: GPS map, pace/distance readouts, AI coach cues.
const ActiveRunSession = ({
  activeRunningSession,
  averagePace,
  coachMessages,
  currentIntervalIndex,
  currentPace,
  elapsedSeconds,
  finishRunningSession,
  gpsTrack,
  intervalElapsed,
  isRunningPaused,
  manualCadence,
  manualDistance,
  manualIncline,
  manualPower,
  manualResistance,
  pauseRunningSession,
  resumeRunningSession,
  screenLocked,
  selectedActivityType,
  setManualCadence,
  setManualDistance,
  setManualIncline,
  setManualPower,
  setManualResistance,
  setScreenLocked,
  setVoiceEnabled,
  totalDistance,
  voiceEnabled,
}) => {
  return (
    <div className="active-run-view mode-data">
      <div className="run-main-display">
        {/* Pulsing live dot + lock */}
        <div className="live-indicator">
          <span className="live-dot" />
          <span className="live-text">LIVE</span>
          <button className="lock-screen-btn" onClick={() => setScreenLocked(!screenLocked)}
            style={{marginLeft:'auto', background:'none', border:'none', cursor:'pointer', color: screenLocked ? '#00D4FF' : 'rgba(255,255,255,0.4)', padding:4}}>
            {screenLocked ? <Lock size={18} /> : <Unlock size={18} />}
          </button>
        </div>
        {/* Live GPS map */}
        {gpsActivityTypes.includes(activeRunningSession.activity_type || selectedActivityType) && gpsTrack.length >= 2 && (
          <RunMap gpsPoints={gpsTrack} isLive={true} height={180} />
        )}
        {/* Dominant pace with color coding */}
        <div className="run-pace-big">
          {(() => {
            const ci = activeRunningSession?.plan_snapshot?.intervals?.[currentIntervalIndex];
            let paceClass = '';
            if (ci?.target_pace_min && ci?.target_pace_max && currentPace > 0) {
              if (currentPace >= ci.target_pace_min && currentPace <= ci.target_pace_max) paceClass = 'pace-in-zone';
              else if (currentPace > ci.target_pace_max) paceClass = 'pace-slow';
              else paceClass = 'pace-fast';
            }
            return <span className={`run-big-number ${paceClass}`}>{currentPace > 0 ? currentPace.toFixed(1) : "--"}</span>;
          })()}
          <span className="run-big-label">min/km</span>
        </div>
        {/* 2x2 stats grid */}
        <div className="run-stats-grid-2x2">
          <div className="run-stat-box-v2">
            <span className="run-stat-value">{totalDistance.toFixed(2)}</span>
            <span className="run-stat-unit">km</span>
          </div>
          <div className="run-stat-box-v2">
            <span className="run-stat-value">{formatRunTime(elapsedSeconds)}</span>
            <span className="run-stat-unit">czas</span>
          </div>
          <div className="run-stat-box-v2">
            <span className="run-stat-value">{averagePace > 0 ? averagePace.toFixed(1) : "--"}</span>
            <span className="run-stat-unit">sr. tempo</span>
          </div>
          <div className="run-stat-box-v2">
            <span className="run-stat-value">{elapsedSeconds > 0 ? Math.round(elapsedSeconds / 60 * 8) : "--"}</span>
            <span className="run-stat-unit">kcal</span>
          </div>
        </div>
      </div>

      {/* Split times table */}
      {activeRunningSession?.plan_snapshot?.intervals && currentIntervalIndex > 0 && (
        <div className="split-times">
          <h4 className="split-title">Splity</h4>
          {activeRunningSession.plan_snapshot.intervals.slice(0, currentIntervalIndex).map((iv, idx) => {
            const paceVal = iv.target_pace_min ? ((iv.target_pace_min + (iv.target_pace_max || iv.target_pace_min)) / 2) : 0;
            const barWidth = paceVal > 0 ? Math.max(10, Math.min(100, 100 - (paceVal - 4) * 20)) : 0;
            return (
              <div key={idx} className="split-row">
                <span className="split-number">{idx + 1}</span>
                <span className="split-type">{intervalTypeNames[iv.type] || iv.type}</span>
                {barWidth > 0 && (
                  <div className="split-pace-bar">
                    <div className="split-pace-fill" style={{ width: `${barWidth}%` }} />
                  </div>
                )}
                <span className="split-time">{formatRunTime(iv.duration_seconds)}</span>
              </div>
            );
          })}
        </div>
      )}

      {activeRunningSession.plan_snapshot?.intervals && currentIntervalIndex < activeRunningSession.plan_snapshot.intervals.length && (() => {
        const ci = activeRunningSession.plan_snapshot.intervals[currentIntervalIndex];
        const progress = Math.min(100, (intervalElapsed / ci.duration_seconds) * 100);
        const remaining = Math.max(0, ci.duration_seconds - intervalElapsed);
        return (
          <Card className={`current-interval-card interval-${ci.type}`}>
            <CardContent>
              <div className="interval-progress-header">
                <span className="interval-active-name">{intervalTypeNames[ci.type] || ci.type}</span>
                <span className="interval-counter">{currentIntervalIndex + 1} / {activeRunningSession.plan_snapshot.intervals.length}</span>
              </div>
              <Progress value={progress} className="interval-progress-bar" />
              <span className="interval-remaining">{formatRunTime(remaining)} pozostało</span>
              {ci.target_pace_min && ci.target_pace_max && (
                <span className="interval-target">Cel: {ci.target_pace_min.toFixed(1)}-{ci.target_pace_max.toFixed(1)} min/km</span>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* GPS bez sygnału (brak uprawnienia, słaby odbiór w budynku) po minucie
          treningu — bez tego jedyną informacją był trening zapisany z zerowym
          dystansem, wyglądający jakby w ogóle się nie nagrał. */}
      {gpsActivityTypes.includes(activeRunningSession.activity_type || selectedActivityType)
        && totalDistance === 0 && elapsedSeconds > 60 && (
        <Card className="manual-metrics-card" data-testid="gps-fallback-distance">
          <CardContent>
            <h3 style={{fontSize:'0.875rem', fontWeight:600, marginBottom:4}}>GPS nie namierzył dystansu</h3>
            <p style={{fontSize:'0.75rem', opacity:0.7, marginBottom:8}}>
              Sprawdź uprawnienia do lokalizacji albo wpisz dystans ręcznie.
            </p>
            <div className="field">
              <Label>Dystans (km)</Label>
              <Input type="number" step="0.1" placeholder="0.0" value={manualDistance} onChange={e => setManualDistance(e.target.value)} />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Manual metrics for non-GPS activities */}
      {!gpsActivityTypes.includes(activeRunningSession.activity_type || selectedActivityType) && (
        <Card className="manual-metrics-card">
          <CardContent>
            <h3 style={{fontSize:'0.875rem', fontWeight:600, marginBottom:8}}>
              {activityTypeLabels[activeRunningSession.activity_type || selectedActivityType]} — metryki
            </h3>
            <div className="manual-metrics-grid">
              <div className="field">
                <Label>Dystans (km)</Label>
                <Input type="number" step="0.1" placeholder="0.0" value={manualDistance} onChange={e => setManualDistance(e.target.value)} />
              </div>
              {(activeRunningSession.activity_type || selectedActivityType) === "treadmill" && (
                <div className="field">
                  <Label>Nachylenie (%)</Label>
                  <Input type="number" step="0.5" placeholder="0" value={manualIncline} onChange={e => setManualIncline(e.target.value)} />
                </div>
              )}
              {(activeRunningSession.activity_type || selectedActivityType) === "ergometer" && (
                <>
                  <div className="field">
                    <Label>Opór</Label>
                    <Input type="number" placeholder="1-20" value={manualResistance} onChange={e => setManualResistance(e.target.value)} />
                  </div>
                  <div className="field">
                    <Label>Kadencja (RPM)</Label>
                    <Input type="number" placeholder="0" value={manualCadence} onChange={e => setManualCadence(e.target.value)} />
                  </div>
                  <div className="field">
                    <Label>Moc (W)</Label>
                    <Input type="number" placeholder="0" value={manualPower} onChange={e => setManualPower(e.target.value)} />
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {coachMessages.length > 0 && (
        <div className={`coach-bubble coach-${coachMessages[coachMessages.length - 1].urgency || "normal"}`}>
          <Volume2 size={16} />
          <span>{coachMessages[coachMessages.length - 1].message}</span>
        </div>
      )}

      <div className="run-controls" style={screenLocked ? {pointerEvents:'none', opacity:0.4} : {}}>
        <Button variant="outline" size="icon" onClick={() => setVoiceEnabled(!voiceEnabled)}>
          {voiceEnabled ? <Volume2 size={20} /> : <VolumeX size={20} />}
        </Button>
        {isRunningPaused ? (
          <Button className="run-resume-btn" onClick={resumeRunningSession}><Play size={24} /> Wznów</Button>
        ) : (
          <Button className="run-pause-btn" onClick={pauseRunningSession}><Pause size={24} /> Pauza</Button>
        )}
        <Button variant="destructive" onClick={finishRunningSession}><StopCircle size={20} /> Zakończ</Button>
      </div>
    </div>
  );
};

export default ActiveRunSession;
